import { FC, FormEvent, useRef } from 'react';
import { useState, useEffect } from 'react';
import { TextField, Box } from '@mui/material';
import { IRepeaterForm } from '../repeater/Repeater';
import { ProductHighlight } from 'src/types/vendorBooth';
import ItemTypeAhead from '../controls/ItemTypeAhead';
import useVendor from 'src/hooks/useVendor';
import { Item } from 'src/types/itemList';
// import { Editor, EditorTools, EditorChangeEvent } from '@progress/kendo-react-editor';
import ItemApi from 'src/api/ItemApi';
import TextLabel from '../TextLabel';
import useSnackbar from 'src/hooks/useSnackbar';

const primaryPath = 'https://www.bfgsupply.com/img/product/thumb/';

interface Props extends IRepeaterForm {
  onSave?: (formData: ProductHighlight) => void;
  data?: ProductHighlight;
}

const ProductHighlightsForm: FC<Props> = (props) => {
  const [number, setNumber] = useState<string>(''); //item number
  const [itemName, setItemName] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [subtitle, setSubtitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const [image, setImage] = useState<string>('');
  const [imgUrl, setImgUrl] = useState<string>(primaryPath + image);

  const inEdit = useRef(false);
  const { vendor } = useVendor();
  const { openSnackBar } = useSnackbar();

  const save = (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    props.onSave({
      itemNumber: number,
      title,
      subtitle,
      description,
      name: itemName,
    });
  };

  useEffect(() => {
    if (props.data) {
      inEdit.current = props.data.inEdit ?? false;

      if (props.data.itemNumber) {
        ItemApi.getByItemNumber(props.data.itemNumber)
          .then((item) => {
            if (item == null) return;

            setNumber(item.itemNumber);
            setItemName(item.name ?? '');
            setSubtitle(item.catalogSubHeader ?? '');
            setDescription(item.description ?? '');
            setTitle(item.catalogHeader ?? '');

            if (item?.images?.length ?? 0 > 0) {
              setNumber(item.itemNumber);
              setImage(item.images[0]);
              setImgUrl(primaryPath + item.images[0]);
            }
          })
          .catch((err) => {
            console.log(err);
            openSnackBar(`Failed to load item data. ${err}`, 'error', 10000);
          });
      }
    }
  }, [props.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (item: Item) => {
    if (item == null) {
      setNumber('');
      setItemName('');
      setSubtitle('');
      setDescription('');
      setTitle('');
      setImage('');
      setImgUrl(primaryPath + image);
      props.onSave({
        itemNumber: '',
        title: '',
        subtitle: '',
        description: '',
        name: '',
        inEdit: true,
      });
      return;
    }

    setNumber(item.itemNumber ?? '');
    setItemName(item.name ?? '');
    setSubtitle(item.catalogSubHeader ?? '');
    setDescription(item.description ?? '');
    setTitle(item.catalogHeader ?? '');

    if (item.images?.length ?? 0 > 0) {
      console.log(item.images[0]);
      setImage(item.images[0]);
      setImgUrl(primaryPath + item.images[0]);
    }

    if (item.image) {
      //item.image is a comma separated string of image names, take the first one and set the item
      const images = item.image.split(',');
      setImage(images[0]);
      setImgUrl(primaryPath + images[0]);
    }

    props.onSave({
      itemNumber: item.itemNumber,
      title: item.catalogHeader,
      subtitle: item.catalogSubHeader,
      description: item.description,
      name: item.name,
      inEdit: true,
    });
  };

  const categoryManager = vendor?.categoryManagers && vendor.categoryManagers.length > 0 && vendor.categoryManagers.find((cm) => cm.email != null && cm.fullName != null);
  const catMangerMailTo = categoryManager?.email ? `mailto:${categoryManager?.email}` : '';

  return (
    <form onSubmit={(e) => save(e)} className="product-highlights-form">
      <Box sx={{ p: 1, background: '#ccc' }}>
        {number ? (
          <Box sx={{ mb: 3 }}>
            <ItemTypeAhead onChange={onChange} vendorNumber={vendor.number} style={{ width: '100%' }} />
          </Box>
        ) : (
          <Box>
            <ItemTypeAhead onChange={onChange} vendorNumber={vendor.number} style={{ width: '100%' }} />
          </Box>
        )}

        {number && (
          <>
            <Box sx={{ mb: 3 }}>
              <TextLabel>Item Name</TextLabel>
              <TextField fullWidth={true} disabled={true} className="is-disabled" value={itemName} required onChange={(e) => setItemName(e.target.value)} variant="outlined" />
            </Box>

            <TextLabel>Item Description</TextLabel>
            <Box component="fieldset" sx={{ mb: 3, background: 'white' }} className="product-highlights-form__description">
              <div dangerouslySetInnerHTML={{ __html: description }} />
            </Box>

            <Box sx={{ pl: 1 }} className="product-highlights-form__image">
              <img
                src={imgUrl}
                onError={() => {
                  setImgUrl('https://www.bfgsupply.com/content/img/img_not_avail.png');
                }}
              />
            </Box>

            <div style={{ fontSize: 'small' }}>
              <i>
                <b>
                  * To change an item's image please email BFG Marketing: <a href="mailto: marketing@bfgsupply.com">marketing@bfgsupply.com</a>, (
                  <a href="https://www.bfgsupply.com/media/6183/bfg-image-specs.pdf" target="_blank">
                    Image Specs
                  </a>
                  )
                </b>
              </i>
              <br />
              <i>
                {categoryManager == null ? (
                  <b>
                    * To change an item's description or name, contact your category manager. (
                    <a href="https://www.bfgsupply.com/media/6183/bfg-image-specs.pdf" target="_blank">
                      Image Specs
                    </a>
                    )
                  </b>
                ) : (
                  <b>
                    * To change an item's description or name, contact your category manager: {categoryManager.fullName} at <a href={catMangerMailTo}>{categoryManager.email}</a>, (
                    <a href="https://www.bfgsupply.com/media/7059/bfg-description-formatting.pdf" target="_blank">
                      Description Guidelines
                    </a>
                    )
                  </b>
                )}
              </i>
            </div>
          </>
        )}
      </Box>
    </form>
  );
};

export default ProductHighlightsForm;
